import { ApolloError } from "@apollo/client";
import { Box, Grid, TextField, Typography, useTheme } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useMemo, useState } from "react";

import StyledDialog from "../../../common/components/dialog/StyledDialog";
import DeviceTemplateNodeSelect from "../../../common/components/layout/components/DeviceTemplateNodeSelect";
import DeviceTemplateSelect from "../../../common/components/layout/components/DeviceTemplateSelect";
import { useGetNodesWithoutDeviceByCustomer } from "../../../common/components/select/NodeSelect/useGetNodesWithoutDeviceByCustomer";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { checkDuplicateDeviceNames } from "../../../common/utils/checkDuplicateDeviceNames";
import { mapTemplateDataToDeviceInput } from "../../../common/utils/mapTemplateDataToDeviceInput";
import { mapTemplateDataToServiceInput } from "../../../common/utils/mapTemplateDataToServiceInput";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useGetDevicesByLocation } from "../../customer-settings/tabs/locations/hooks/useGetDevicesByLocation";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { useCreateBatchDevices } from "../hooks/useCreateBatchDevices";
import { useCreateBatchServices } from "../hooks/useCreateBatchServices";
import { validateCreateDevicesFromTemplate } from "../../../common/utils/validateCreateDevicesFromTemplate";
import { useGetCustomer } from "../../../common/hooks/useGetCustomer";
import { DEFAULT_DEVICE_PASSWORD } from "../../../common/variables/common";

export type TemplateDevice = {
  deviceType: string;
  deviceIp: string;
  nodeNumber: number;
  model: string[];
};

interface CreateDevicesFromTemplateDialogProps {
  open: boolean;
  handleClose: () => void;
}

const CreateDevicesFromTemplateDialog = ({
  open,
  handleClose,
}: CreateDevicesFromTemplateDialogProps) => {
  const theme = useTheme();
  const confirm = useConfirm();
  const { customer } = useGetCustomer();
  const selectedCustomerId = useCustomerIdGuard();
  const selectedLocation = useSelectedLocationDropdown();
  const { data } = useGetNodesWithoutDeviceByCustomer();
  const { data: currentDeviceData, refetchDevices } = useGetDevicesByLocation(
    selectedLocation?.value ?? ""
  );

  const { syncShadowsForNode } = useSyncShadow();

  const { batchCreateDevices, loading } = useCreateBatchDevices();

  const { batchCreateServices } = useCreateBatchServices();

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [devicePassword, setDevicePassword] = useState("");

  const [selectedTemplate, setSelectedTemplate] = useState<{
    id: string;
    templateData: string;
    numberOfNodes: number;
  } | null>(null);

  const [parsedTemplateData, setParsedTemplateData] = useState<
    TemplateDevice[] | null
  >(null);

  const [selectedNodes, setSelectedNodes] = useState<
    { nodeId: string; nodeIP: string; nodeName: string }[]
  >([]);

  useEffect(() => {
    if (customer) {
      setDevicePassword(customer?.devicePassword ?? DEFAULT_DEVICE_PASSWORD);
    }
  }, [customer]);

  const resetDialogState = () => {
    setHasSubmitted(false);

    setDevicePassword(customer?.devicePassword ?? DEFAULT_DEVICE_PASSWORD);

    setSelectedTemplate(null);

    setParsedTemplateData(null);

    setSelectedNodes([]);
  };

  const closeDialog = (): void => {
    handleClose();

    resetDialogState();
  };

  const confirmDialogClose = (): void => {
    confirm({
      title: "Are you sure you want to cancel?",
      confirmationText: "Cancel",
      cancellationText: "Back",
      ...confirmDialogStyleOptions,
      confirmationButtonProps: {
        ...confirmDialogStyleOptions.confirmationButtonProps,
        color: "primary",
      },
    }).then(async (): Promise<void> => {
      closeDialog();
    });
  };

  const existingNames =
    currentDeviceData?.getDevicesByLocation?.items
      ?.map(d => d?.id?.replace(/^DE#/, "").toLowerCase())
      .filter((id): id is string => !!id) ?? [];

  const filteredNodes = useMemo(() => {
    if (!selectedTemplate?.numberOfNodes || !data) return [];

    return data.getNodesWithoutDeviceByCustomer.items
      ?.filter(node => node?.locationId === selectedLocation?.value)
      ?.map(node => ({
        nodeId: node?.id ?? "",
        nodeIP: node?.nodeIP ?? "",
        nodeName: node?.nodeName ?? "",
      }))
      .filter(n => n.nodeId && n.nodeIP && n.nodeName);
  }, [data, selectedLocation?.value, selectedTemplate]);

  const handleNodeSelect = (
    index: number,
    node: { nodeId: string; nodeIP: string; nodeName: string }
  ) => {
    const updated = [...selectedNodes];

    updated[index] = node;

    setSelectedNodes(updated);
  };

  const saveDevice = async (): Promise<void> => {
    setHasSubmitted(true);

    const validation = validateCreateDevicesFromTemplate({
      parsedTemplateData,
      selectedNodes,
      devicePassword,
    });

    if (!validation.isValid) {
      errorNotification(validation.errorMessage!);

      return;
    }

    try {
      const devicesToCreate = mapTemplateDataToDeviceInput({
        templateData: parsedTemplateData!,
        selectedCustomerId,
        selectedLocation: selectedLocation ?? { title: "", value: "" },
        selectedNodes,
        devicePassword,
      });

      const uniqueDevices = checkDuplicateDeviceNames(
        existingNames,
        devicesToCreate
      );

      const servicesToCreate = mapTemplateDataToServiceInput(
        uniqueDevices,
        parsedTemplateData!
      );

      await batchCreateDevices(uniqueDevices);

      const serviceResponse = await batchCreateServices(servicesToCreate);

      if (serviceResponse?.errors) {
        errorNotification("Failed to create services");

        return;
      }

      closeDialog();

      successNotification("Devices created from template!");

      try {
        await Promise.all(
          selectedNodes.map(async ({ nodeId }) => {
            if (nodeId) {
              const formattedNodeId = nodeId.replaceAll("_", "#");

              await syncShadowsForNode(formattedNodeId);
            }
          })
        );
      } catch (error) {
        console.error(error);
      }
    } catch (error) {
      if (
        (error as ApolloError).message.includes(
          "The conditional request failed"
        )
      ) {
        errorNotification("Device ID already exists");
      } else {
        errorNotification((error as ApolloError).message);
      }
    }
  };

  const handleTemplateChange = ({
    id,
    templateData,
    numberOfNodes,
  }: {
    id: string;
    templateData: string;
    numberOfNodes: number;
  }) => {
    try {
      const parsedData = JSON.parse(templateData);

      setSelectedTemplate({ id, templateData, numberOfNodes });

      setParsedTemplateData(parsedData);

      refetchDevices(selectedLocation?.value ?? "");

      setSelectedNodes(
        Array(numberOfNodes).fill({
          nodeId: "",
          nodeIP: "",
          nodeName: "",
        })
      );

      setHasSubmitted(false);
    } catch (error) {
      errorNotification("Invalid template data format");
    }
  };

  return (
    <StyledDialog
      open={open}
      title="Create new devices from template"
      onClose={confirmDialogClose}
      SubmitButtonProps={{
        loading,
        onSubmit: saveDevice,
      }}
    >
      <Grid container justifyContent="center" direction="column" spacing={2}>
        <Grid item>
          <Typography variant="body1">Location</Typography>
          <Box
            sx={{
              color: theme.palette.text.primary,
              fontWeight: "bold",
            }}
          >
            {selectedLocation?.title}
          </Box>
        </Grid>

        <Grid item>
          <DeviceTemplateSelect
            templateId={selectedTemplate?.id ?? ""}
            onTemplateChange={handleTemplateChange}
            error={hasSubmitted && !selectedTemplate?.id}
            helperText={
              hasSubmitted && !selectedTemplate?.id
                ? "Device template selection is required"
                : ""
            }
          />
        </Grid>

        {selectedTemplate && selectedTemplate.numberOfNodes > 0 && (
          <>
            <Grid item>
              <Typography variant="body1">Assign Nodes</Typography>
              <Grid container spacing={2} direction="column">
                {selectedNodes.map((node, index) => {
                  return (
                    <DeviceTemplateNodeSelect
                      key={node.nodeId || index}
                      index={index}
                      selectedNodeId={node.nodeId}
                      filteredNodes={filteredNodes}
                      onChange={node => handleNodeSelect(index, node)}
                      error={hasSubmitted && !node.nodeId}
                      helperText={
                        hasSubmitted ? "Node selection is required" : ""
                      }
                    />
                  );
                })}
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant="body1">Device Password</Typography>
              <Box mt={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={devicePassword}
                  onChange={e => setDevicePassword(e.target.value)}
                  error={hasSubmitted && !devicePassword}
                  helperText={
                    hasSubmitted && !devicePassword
                      ? "Device password is required"
                      : ""
                  }
                  placeholder={devicePassword || DEFAULT_DEVICE_PASSWORD}
                />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </StyledDialog>
  );
};

export default CreateDevicesFromTemplateDialog;
